import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  BadgeCheck,
  Building2,
  CheckCircle2,
  Clock3,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRoundCog,
  XCircle,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { getOrganizationTranslation } from "../context/organizationOnboardingTranslations";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const getSession = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

const AdminRequests = () => {
  const { t, lang } = useLanguage();
  const tr = (key, fallback) =>
    t?.ui?.adminRequests?.[key] ||
    t?.[key] ||
    getOrganizationTranslation(lang, "adminRequests", key, fallback);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const session = getSession();
  const isSuperAdmin = Boolean(session?.isSuperAdmin || session?.adminRole === "SuperAdmin");

  const loadRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/api/requests/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await response.json().catch(() => []);
      if (!response.ok) throw new Error(data.message || "Unable to load requests");
      setRequests(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((request) => {
      const matchesStatus = status === "all" || request.status === status;
      const matchesSearch =
        !q ||
        [
          request.name,
          request.email,
          request.organizationNameSnapshot,
          request.organization,
          request.requestedOrganizationName,
          request.accountType,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [requests, search, status]);

  const processRequest = async (request, action) => {
    const confirmText =
      action === "approve"
        ? request.accountType === "Admin"
          ? tr(
              "approveAdminConfirm",
              "Approuver cette demande Administrateur et envoyer l’email de création d’organisation ?"
            )
          : tr(
              "approveClientConfirm",
              "Approuver cette demande Client et envoyer l’invitation de création de compte ?"
            )
        : tr("rejectConfirm", "Rejeter cette demande ?");
    if (!window.confirm(confirmText)) return;

    setProcessingId(request._id);
    setError("");
    try {
      const response = await fetch(`${API_URL}/api/requests/${action}/${request._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Action failed");
      await loadRequests();
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setProcessingId("");
    }
  };

  const formatDate = (value) =>
    new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));

  const badge = (requestStatus) => {
    const styles = {
      pending: "bg-amber-50 text-amber-700 ring-amber-200",
      approved: "bg-blue-50 text-blue-700 ring-blue-200",
      rejected: "bg-rose-50 text-rose-700 ring-rose-200",
      registered: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    };
    return (
      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-black ring-1 ${styles[requestStatus] || styles.pending}`}>
        {requestStatus}
      </span>
    );
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="rounded-[2rem] bg-slate-950 p-7 text-white shadow-2xl sm:p-9">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-xs font-black text-cyan-200">
                {isSuperAdmin ? <ShieldCheck className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                {isSuperAdmin ? tr("platformScope", "Toutes les organisations") : tr("organizationScope", "Mon organisation")}
              </div>
              <h1 className="mt-5 text-3xl font-black tracking-tight sm:text-4xl">
                {tr("title", "Demandes d’accès")}
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                {isSuperAdmin
                  ? tr("superAdminHint", "Le Super Admin traite les demandes Admin et peut superviser les demandes Client.")
                  : tr("orgAdminHint", "Vous voyez uniquement les demandes Client destinées à votre organisation.")}
              </p>
            </div>
            <button onClick={loadRequests} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-black text-slate-950">
              <RefreshCw className="h-4 w-4" />
              {tr("refresh", "Actualiser")}
            </button>
          </div>
        </header>

        {error && (
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-800">
            <AlertCircle className="h-5 w-5" /> {error}
          </div>
        )}

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={tr("search", "Nom, email, organisation...")} className="w-full rounded-xl border border-slate-200 py-3 pl-11 pr-4 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" />
            </div>
            <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700">
              <option value="all">{tr("allStatuses", "Tous les statuts")}</option>
              <option value="pending">pending</option>
              <option value="approved">approved</option>
              <option value="registered">registered</option>
              <option value="rejected">rejected</option>
            </select>
          </div>
        </section>

        {loading ? (
          <div className="mt-8 flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white p-12 font-bold text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            {tr("loading", "Chargement...")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-14 text-center">
            <Clock3 className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-4 font-black text-slate-900">{tr("none", "Aucune demande")}</p>
          </div>
        ) : (
          <div className="mt-6 grid gap-4">
            {filtered.map((request) => {
              const organizationName =
                request.organizationRef?.name ||
                request.organizationNameSnapshot ||
                request.requestedOrganizationName ||
                request.organization ||
                "—";
              const isProcessing = processingId === request._id;
              return (
                <article key={request._id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-black ${request.accountType === "Admin" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`}>
                          {request.accountType === "Admin" ? <UserRoundCog className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
                          {request.accountType || "Client"}
                        </span>
                        {badge(request.status)}
                      </div>
                      <h2 className="mt-3 truncate text-lg font-black text-slate-950">{request.name}</h2>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" />{request.email}</span>
                        <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" />{organizationName}</span>
                      </div>
                      <p className="mt-2 text-xs text-slate-400">{formatDate(request.createdAt)}</p>
                      {request.reason && <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm leading-6 text-slate-600">{request.reason}</p>}
                    </div>

                    {request.status === "pending" && (
                      <div className="flex shrink-0 gap-2">
                        <button disabled={isProcessing} onClick={() => processRequest(request, "reject")} className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-black text-rose-700 disabled:opacity-50">
                          <XCircle className="h-4 w-4" /> {tr("reject", "Rejeter")}
                        </button>
                        <button disabled={isProcessing} onClick={() => processRequest(request, "approve")} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-black text-white disabled:opacity-50">
                          {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                          {tr("approve", "Approuver")}
                        </button>
                      </div>
                    )}
                    {request.status === "registered" && <BadgeCheck className="h-7 w-7 text-emerald-600" />}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminRequests;
